// originated from FileManagerProvider.js
import React, { Component, createContext } from 'react';
import { plural, t } from '@lingui/macro';
import { connect } from 'react-redux';
import { message } from 'antd';
import fileSize from 'filesize';
import FileActions from 'bukku/redux/File/actions';
import { withI18n } from "@lingui/react";
import { FileViewHelper } from './helper.view';
import queryString from "query-string";
import { history } from "bukku/redux/store";
import QuotaActions from 'bukku/redux/Quota/actions';
import { UsageType } from '../../containers/AppPage/ControlPanel/Subscription/QuotaSubscription/UsageType';
import { IMAGE_ACCEPT_EXTENSIONS } from '../Upload/constants';

const {
    readGlobalFiles,
    resetFileManager,
} = FileActions;

const {
    readQuotas,
    setStorageQuota,
} = QuotaActions;

const apiUrl = process.env.REACT_APP_PROXY_API_URL;
// const FILE_SELECTION_LIMIT = 20;

export const FileContext = createContext({contextState: {}, contextAction: {}, contextProps: {}});

/* eslint-disable react/no-unused-state */ // states might be using in context consumer, do not delete
class ContextProvider extends Component {
    constructor(props) {
        super(props);

        const {location} = window;
        const {page, page_size} = queryString.parse(location.search);

        this.state = {
            rowList: [],
            rowIdList: [],
            search: undefined,

            // for infinite scroll in FileSelect
            infiniteScrollFileList: [],
            hasMore: true,

            // for pagination in FileManager
            paginationFileList: [],

            // for both FileSelect and FileManager
            currentPage: parseInt(page, 10) || 1,
            currentPageSize: parseInt(page_size, 10) || 30,

            // for <Spin>
            loading: false,
            uploading: [],
            isSearching: false,
            isScrolling: false,
            isFirstLoad: true,

            // for animations
            newlyUploadedIds: [],
        };
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            // parent
            fileTypeFilter,
            visible,
            // redux
            readGlobalFiles,
            readGlobalFilesState,
            deleteFilesState,
            deletedFileIds,
            quotas,
            globalFiles,
            readQuotas,
            setStorageQuota,
        } = this.props;

        const {
            isSearching,
            isScrolling,
            isFirstLoad,
        } = this.state;

        if (visible && !prevProps.visible) {
            this.setState({ isFirstLoad: true });
            readQuotas();
            readGlobalFiles({ page: 1, type: fileTypeFilter, pageSize: 30 });
        }

        // changes to the file manager. (uploaded new file, search, scroll to next page,)
        if (readGlobalFilesState === 'success' && prevProps.readGlobalFilesState === 'request') {
            // isFirstLoad = every time the modal is first opened, including after closed
            if (isFirstLoad) {
                this.setState({
                    infiniteScrollFileList: globalFiles,
                    isFirstLoad: false,
                    paginationFileList: globalFiles,
                });
            }
            // new search term
            else if (isSearching) {
                this.onSearchSuccess();
            }
            // infinite scroll, load more files
            else if (isScrolling) {
                this.onInfiniteScrollNextPageSuccess();
            }
            // pagination changed page/page_size, set new file list data
            else {
                this.setState({ paginationFileList: globalFiles });
            }
        }

        // only for FileManager
        if (deleteFilesState === 'success' && prevProps.deleteFilesState === 'request') {
            const deletedFiles = prevState.paginationFileList
                .filter(f => deletedFileIds.includes(f.id)); // clear deleted files
            const deletedFileSpace = deletedFiles.reduce((acc, file) => acc + file.size, 0);

            const currentStorageQuota = quotas.find(q => q.type === UsageType.STORAGE);
            const updatedStorageQuota = {
                ...currentStorageQuota,
                utilisation: currentStorageQuota.utilisation - deletedFileSpace,
            };

            setStorageQuota(updatedStorageQuota);

            message.success(
                `
                ${plural(deletedFiles.length, { one: '1 file deleted.', other: '# files deleted.' })} 
                ${t`${fileSize(deletedFileSpace)} freed.`}
                `,
            );
            this.onFileDeleteSuccess(deletedFileIds); // setState
        }
    }

    // only for FileManager
    onFileDeleteSuccess = deletedFileIds => {
        // timeout for fadeOut animation in FileCard
        setTimeout(() => {
            // remove from file Manager
            this.setState(prevState => ({
                paginationFileList: prevState.paginationFileList
                    .filter(f => !deletedFileIds.includes(f.id)), // clear deleted files
            }),

            () => {
                // after deleting the files, get the new page and page_size data
                this.getGlobalFiles();
            },
            );
        }, 300);

        // remove from selection
        this.setState(prevState => (
            {
                rowList: prevState.rowList.filter(f => !deletedFileIds.includes(f.id)),
                rowIdList: prevState.rowIdList.filter(id => !deletedFileIds.includes(id)),
            }));
    };

    // search for file
    onSearch = value => {
        this.setState({
            loading: true,
            search: value,
            isSearching: true,
        });
        this.getGlobalFiles(true, value);
        FileViewHelper.scrollToTop();
    };

    onSearchSuccess = () => {
        const {
            globalFiles,
            pagination,
        } = this.props;

        const {
            currentPageSize,
        } = this.state;

        const {location} = window;

        this.setState(prevState => ({
            loading: false,
            isSearching: false,

            currentPage: 1, // restart from page 1
            currentPageSize: prevState.currentPageSize,

            // infinte scroll
            infiniteScrollFileList: globalFiles, // clear previously loaded pages, start from top
            hasMore: !(globalFiles.length >= pagination.total), // stop infinite scroll when no more next page

            // pagination
            paginationFileList: globalFiles,
        }));

        // update the url with current page size (not showing if page size = 30)
        if (currentPageSize !== 30) {
            const newParams = {page_size: currentPageSize};
            const paramString = queryString.stringify(newParams, { arrayFormat: 'bracket', skipNull: true });

            const searchString = `?${paramString}`;

            if (location.search !== searchString)
                history.replace(location.pathname + searchString);
        }
    };

    // Pagination Changed, only for FileManager
    handlePaginationChange = (nextPage, pageSize) => {
        this.setState({
            loading: false,
            isScrolling: false,
            currentPage: nextPage,
            currentPageSize: pageSize,
        },
        () => {
            this.getGlobalFiles();
        },
        );
    };

    // Infinite Scroll pagination Changed, only for FileSelect
    handleInfiniteOnLoad = () => {
        const {
            search,
            currentPage,
            infiniteScrollFileList,
        } = this.state;
        const {
            //parent
            fileTypeFilter,
            // redux
            readGlobalFiles,
            pagination,
        } = this.props;
            // message.warning(t`Loading new files`);

        if (infiniteScrollFileList.length >= pagination.total) {
            // message.warning(t`Loaded all files`);
            this.setState({ hasMore: false });
            return;
        }
        this.setState({
            loading: true,
            isScrolling: true,
        },
        () => {
            readGlobalFiles({
                page: currentPage + 1,
                type: fileTypeFilter,
                search,
            });
        },
        );
    };

    // only for FileSelect
    onInfiniteScrollNextPageSuccess = () => {
        const {
            globalFiles,
        } = this.props;
        this.setState(prevState => ({
            currentPage: prevState.currentPage + 1,
            infiniteScrollFileList: prevState.infiniteScrollFileList.concat(globalFiles),
            loading: false,
            isScrolling: false,
        }));
    };

    // Build header with authorisation & stuffs.
    getHeaders = () => {
        const headers = {};
        const {
            // redux
            accessToken,
            subdomain,
        } = this.props;
        if (accessToken)
            headers.Authorization = `Bearer ${accessToken}`;
        if (subdomain)
            headers['Company-Subdomain'] = subdomain;
        return headers;
    };

    getApiUrl = () => `${apiUrl}/files`;

    // Do checking following the rules.
    beforeUpload = file => {
        const {
            // parent
            acceptedMimes,
        } = this.props;

        if (acceptedMimes && acceptedMimes.indexOf(file.type) === -1) {
            message.error(t`The file type is blocked for this upload.`);
            return false;
        }

        if ((file.size / 1024 / 1024) > this.fileLimitMB) {
            message.error(t`The file exceeds the file size limit at ${this.fileLimitMB}MB.`);
            return false;
        }
        return true;
    };

    getGlobalFiles = (isSearchFile = false, searchValue = null) => {
        const {
            search,
            currentPage,
            currentPageSize,
        } = this.state;
        const {
            //parent
            fileTypeFilter,
            // redux
            readGlobalFiles,
        } = this.props;

        readGlobalFiles({
            // if isSearchFile, reset it to page 1
            page: isSearchFile ? 1 : currentPage,
            type: fileTypeFilter,
            search: isSearchFile ? searchValue : search,
            page_size: currentPageSize,
        });
    };

    onUploadChange = info => {
        const {
            // redux
            quotas,
            setStorageQuota,
        } = this.props;

        if (info.file.status === 'uploading') {
            const skeletonFile = info.file;
            // eslint-disable-next-line no-underscore-dangle
            skeletonFile._isSkeleton = true;

            this.setState(prevState => ({
                ...(!prevState.uploading.includes(skeletonFile.uid) && {
                    uploading: [skeletonFile.uid, ...prevState.uploading], // file upload count for loading
                    infiniteScrollFileList: [skeletonFile, ...prevState.infiniteScrollFileList], // a skeleton
                    paginationFileList: [skeletonFile, ...prevState.paginationFileList],
                }),
            }));
        }
        else if (info.file.status !== 'done') {
            const skeletonFile = info.file;

            if (info.file.status === 'error') {
                const data = info?.file?.response;
                message.error(data.message || t`Unknown error occurred.`);
            }
            this.setState(prevState => ({
                uploading: prevState.uploading.filter(uid => uid !== skeletonFile.uid),
                infiniteScrollFileList: prevState.infiniteScrollFileList
                    .filter(f => f.uid !== skeletonFile.uid), // remove skeleton
                paginationFileList: prevState.paginationFileList
                    .filter(f => f.uid !== skeletonFile.uid), // remove skeleton
            }));
        }
        else if (info.file.status === 'done') { // Done
            const currentFile = info.file.response.file;
            currentFile.used_count = 0; //preprocess to allow delete

            this.setState(prevState => ({
                uploading: prevState.uploading.filter(uid => uid !== info.file.uid),
                loading: false,

                // remove skeleton AND add new uploaded file
                infiniteScrollFileList: [currentFile, ...prevState.infiniteScrollFileList]
                    .filter(f => f.uid !== info.file.uid), // add new file, remove skeleton
                hasMore: true,
                paginationFileList: [currentFile, ...prevState.paginationFileList]
                    .filter(f => f.uid !== info.file.uid), // add new file, remove skeleton

                newlyUploadedIds: [currentFile.id, ...prevState.newlyUploadedIds], // for animations
            }),
            () => {
                const {
                    uploading,
                } = this.state;

                const currentStorageQuota = quotas.find(q => q.type === UsageType.STORAGE);
                const updatedStorageQuota = {
                    ...currentStorageQuota,
                    utilisation: currentStorageQuota.utilisation + currentFile.size,
                };

                setStorageQuota(updatedStorageQuota);

                // only re-read when all files are uploaded
                if (uploading.length === 0)
                    this.getGlobalFiles();

                // for File Card css fadeIn animations
                setTimeout(() => {
                    this.setState(prevState => ({
                        newlyUploadedIds: prevState.newlyUploadedIds.filter(id => id !== currentFile.id),
                    }));
                }, 500);
            },
            );
            FileViewHelper.scrollToTop();
        }
    };

    getUploadProps = () => {
        const {
            // parent
            fileTypeFilter,
            mode,
        } = this.props;
        return {
            accept: fileTypeFilter === 'IMAGE' ? IMAGE_ACCEPT_EXTENSIONS : undefined,
            multiple: mode === 'multiple',
            showUploadList: false,
            headers: this.getHeaders(),
            action: this.getApiUrl(),
            beforeUpload: this.beforeUpload,
            onChange: this.onUploadChange,
        };
    };

    // click submit in single mode
    submitFile = file => {
        const {
            onOk,
        } = this.props;

        // preprocess
        onOk(file);
        this.resetModal();
    };

    // when close modal reset state
    resetModal = () => {
        const {
            onCancel,
            resetFileManager,
        } = this.props;

        resetFileManager();

        // reset state
        this.setState({
            rowList: [],
            rowIdList: [],
            search: undefined,

            currentPage: 1,
            currentPageSize: 30,

            // for infinite scroll in FileSelect
            infiniteScrollFileList: [],
            hasMore: true,

            // for pagination in FileManager
            paginationFileList: [],

            // for <Spin>
            loading: false,
            uploading: [],
            isFirstLoad: true,

            // for animations
            newlyUploadedIds: [],
        },
        () => onCancel(),
        );
    };

    onFileCardClick = (item, mode) => {
        const {
            rowIdList,
            // rowList,
        } = this.state;

        switch (mode) {
            case 'multiple':
                // remove from selection
                if (rowIdList.includes(item.id)) {
                    this.setState(prevState => (
                        {
                            rowList: prevState.rowList.filter(f => f.id !== item.id),
                            rowIdList: prevState.rowIdList.filter(id => id !== item.id),
                        }));
                }
                // add to selection
                // else if (rowList.length >= FILE_SELECTION_LIMIT) {
                //     message.warning(
                //         plural(FILE_SELECTION_LIMIT, {
                //             one: 'Cannot select more than 1 file.',
                //             other: 'Cannot select more than # files.',
                //         }),
                //     );
                // }
                else {
                    this.setState(prevState => (
                        {
                            rowList: [...prevState.rowList, item],
                            rowIdList: [...prevState.rowIdList, item.id],
                        }));
                }
                break;
            case 'single':
            default:
                // console.log("Single Select", item)
                this.setState(prevState => (
                    {
                        rowList: [...prevState.rowList, item],
                        rowIdList: [...prevState.rowIdList, item.id],
                    }));
                this.submitFile(item);
                break;
        }
    };

    render() {
        const {
            children,
            isFileManager,
            onOk,
            initFiles,
            mode,
        } = this.props;
        return (
            <FileContext.Provider
                // eslint-disable-next-line react/jsx-no-constructed-context-values
                value={{
                    contextState: this.state,
                    contextAction: {
                        resetModal: this.resetModal,
                        onSearch: this.onSearch,
                        getUploadProps: this.getUploadProps,
                        onFileCardClick: this.onFileCardClick,
                        handlePaginationChange: this.handlePaginationChange,
                        handleInfiniteOnLoad: this.handleInfiniteOnLoad,
                    },
                    contextProps: {
                        onOk,
                        initFiles,
                        mode,
                        isFileManager,
                        ...this.props,
                    },
                }}
            >
                {children}
            </FileContext.Provider>
        );
    }
}
export const FileContextProvider = connect(state => ({
    accessToken: state.Auth.accessToken,
    subdomain: state.Auth.subdomain,
    globalFiles: state.File.globalFiles,
    pagination: state.File.pagination,
    readGlobalFilesState: state.File.readGlobalFilesState,
    deleteFilesState: state.File.deleteFilesState,
    deletedFileIds: state.File.deletedFileIds,
    quotas: state.Quota.quotas,
}), {
    readGlobalFiles,
    resetFileManager,
    readQuotas,
    setStorageQuota,
})(withI18n()(ContextProvider));
